import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import './CreatorAnalyticsPage.css';

interface LearningTimeItem {
    courseId: string;
    courseTitle: string;
    courseTitles: Record<string, string> | null;
    courseThumbnailUrl: string | null;
    totalSeconds: number;
    uniqueLearners: number;
    percentage: number;
}

interface LearningTimeResponse {
    month: string;
    systemTotalSeconds: number;
    creatorTotalSeconds: number;
    creatorPercentage: number;
    items: LearningTimeItem[];
}

const CreatorAnalyticsPage: React.FC = () => {
    const { t, i18n } = useTranslation(['creator', 'profile']);
    const navigate = useNavigate();
    const lang = i18n.language;

    // Current month as YYYY-MM
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1); // 1-based

    const [data, setData] = useState<LearningTimeResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const monthKey = `${year}-${String(month).padStart(2, '0')}`;

    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

    const fetchData = useCallback(async (m: string) => {
        setLoading(true);
        try {
            const res = await apiClient.get<LearningTimeResponse>(
                '/creators/me/learning-time',
                { params: { month: m } },
            );
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(monthKey);
    }, [monthKey, fetchData]);

    const goPrev = () => {
        if (month === 1) {
            setYear((y) => y - 1);
            setMonth(12);
        } else {
            setMonth((m) => m - 1);
        }
    };

    const goNext = () => {
        if (isCurrentMonth) return;
        if (month === 12) {
            setYear((y) => y + 1);
            setMonth(1);
        } else {
            setMonth((m) => m + 1);
        }
    };

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        if (minutes >= 60) {
            return t('profile:stats.timeShortHoursMinutes', {
                h: Math.floor(minutes / 60),
                m: minutes % 60,
            });
        }
        return t('profile:stats.timeShortMinutes', { value: minutes });
    };

    const getMonthLabel = (): string => {
        const date = new Date(year, month - 1, 1);
        return date.toLocaleDateString(lang, { year: 'numeric', month: 'long' });
    };

    const getCourseTitle = (item: LearningTimeItem): string => {
        if (item.courseTitles && item.courseTitles[lang]) {
            return item.courseTitles[lang];
        }
        return item.courseTitle;
    };

    return (
        <div className="creator-analytics-page">
            {/* Header */}
            <div className="ca-header">
                <button className="ca-back" onClick={() => navigate(-1)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1>{t('creator:analytics.title')}</h1>
            </div>

            {/* Month Selector */}
            <div className="ca-month-selector">
                <button className="ca-month-btn" onClick={goPrev}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span className="ca-month-label">{getMonthLabel()}</span>
                <button
                    className="ca-month-btn"
                    onClick={goNext}
                    disabled={isCurrentMonth}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {loading ? (
                <div className="ca-loading">
                    <div className="loading-spinner" />
                    <span>{t('creator:analytics.loading')}</span>
                </div>
            ) : data ? (
                <>
                    {/* Summary Cards */}
                    <div className="ca-summary">
                        <div className="ca-summary-card">
                            <div className="ca-summary-value">
                                {formatTime(data.systemTotalSeconds)}
                            </div>
                            <div className="ca-summary-label">
                                {t('creator:analytics.systemTotal')}
                            </div>
                        </div>
                        <div className="ca-summary-card highlight">
                            <div className="ca-summary-value">
                                {formatTime(data.creatorTotalSeconds)}
                            </div>
                            <div className="ca-summary-label">
                                {t('creator:analytics.myContribution')}
                            </div>
                        </div>
                    </div>

                    {/* Percentage Bar */}
                    <div className="ca-percentage-bar">
                        <div className="ca-percentage-header">
                            <span className="ca-percentage-title">
                                {t('creator:analytics.contributionRate')}
                            </span>
                            <span className="ca-percentage-value">
                                {data.creatorPercentage}%
                            </span>
                        </div>
                        <div className="ca-bar-track">
                            <div
                                className="ca-bar-fill"
                                style={{ width: `${Math.min(data.creatorPercentage, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Course List */}
                    {data.items.length > 0 ? (
                        <>
                            <h2 className="ca-section-title">
                                {t('creator:analytics.courseBreakdown')}
                            </h2>
                            <div className="ca-course-list">
                                {data.items.map((item) => (
                                    <div key={item.courseId} className="ca-course-row">
                                        <div className="ca-course-thumb">
                                            {item.courseThumbnailUrl ? (
                                                <img src={item.courseThumbnailUrl} alt="" />
                                            ) : (
                                                <div className="ca-course-thumb-placeholder">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                        <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="ca-course-info">
                                            <div className="ca-course-title">
                                                {getCourseTitle(item)}
                                            </div>
                                            <div className="ca-course-learners">
                                                {t('creator:analytics.learners', { count: item.uniqueLearners })}
                                            </div>
                                        </div>
                                        <div className="ca-course-stats">
                                            <div className="ca-course-time">
                                                {formatTime(item.totalSeconds)}
                                            </div>
                                            <div className="ca-course-pct">
                                                {item.percentage}%
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="ca-empty">
                            <div className="ca-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p>{t('creator:analytics.noData')}</p>
                        </div>
                    )}
                </>
            ) : (
                <div className="ca-empty">
                    <div className="ca-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p>{t('creator:analytics.loadError')}</p>
                </div>
            )}
        </div>
    );
};

export default CreatorAnalyticsPage;
